import React, { useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    LinearProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Divider,
    Chip
} from '@mui/material';
import {
    Description as DescriptionIcon,
    People as PeopleIcon,
    SwapVert as SwapVertIcon,
    Download as DownloadIcon,
    FilterList as FilterListIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import api from '../utils/api';

const STORAGE_LOCATIONS = ['All', 'Mill', 'Godown', 'Nora'];
const PARTY_LOCATIONS = ['Ladnun', 'Bidasar', 'Parihara', 'Sujangarh', 'Salasar', 'Chhapar'];

// Allow custom party location input
const ALLOW_CUSTOM_PARTY_LOCATION = true;

const ReportsPage = () => {
    const [selectedReport, setSelectedReport] = useState('');
    const [filters, setFilters] = useState({
        location: '',
        startDate: '',
        endDate: '',
        customLocation: '', // For custom party location input
        productLocation: '' // For filtering by product/inventory location
    });
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Order selection states for pending orders report
    const [ordersList, setOrdersList] = useState([]);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [orderSelectionOpen, setOrderSelectionOpen] = useState(false);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [ordersLoaded, setOrdersLoaded] = useState(false);

    const reportTypes = [
        {
            id: 'pending-orders',
            title: 'Pending Orders Report',
            description: 'View all pending orders with "ordered" status products. Select "All" to see products from all locations with location column included.',
            icon: <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            locationLabel: 'Storage Location',
            locations: STORAGE_LOCATIONS,
            requiresLocation: true,
            allowCustomLocation: false
        },
        {
            id: 'party-pending-orders',
            title: 'Party Pending Orders Report',
            description: 'View pending orders grouped by party. You can enter any party location (e.g., city name). Optionally filter by product inventory location.',
            icon: <PeopleIcon sx={{ fontSize: 40, color: 'success.main' }} />,
            locationLabel: 'Party Location',
            locations: PARTY_LOCATIONS,
            requiresLocation: true,
            allowCustomLocation: ALLOW_CUSTOM_PARTY_LOCATION,
            showProductLocationFilter: true
        },
        {
            id: 'stock-movement',
            title: 'Stock Movement Report',
            description: 'View stock movements (sales and purchases) for a specific storage location',
            icon: <SwapVertIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
            locationLabel: 'Storage Location',
            locations: STORAGE_LOCATIONS.filter(loc => loc !== 'All'), // Remove 'All' option for stock movement
            requiresLocation: true,
            allowCustomLocation: false
        }
    ];

    const handleReportSelect = (reportId) => {
        setSelectedReport(reportId);
        // Reset filters when changing report type
        setFilters({
            location: '',
            startDate: '',
            endDate: '',
            customLocation: '',
            productLocation: ''
        });
        // Reset order selection states
        setOrdersList([]);
        setSelectedOrderIds([]);
        setOrdersLoaded(false);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        // Reset orders loaded when filters change for pending orders report
        if (selectedReport === 'pending-orders') {
            setOrdersLoaded(false);
            setOrdersList([]);
            setSelectedOrderIds([]);
        }
    };

    // Fetch orders list for selection (pending orders report only)
    const fetchOrdersList = async () => {
        if (!filters.location) {
            setSnackbar({
                open: true,
                message: 'Please select a storage location first',
                severity: 'warning'
            });
            return;
        }

        setLoadingOrders(true);
        try {
            const params = {
                location: filters.location,
                ...(filters.startDate && { startDate: filters.startDate }),
                ...(filters.endDate && { endDate: filters.endDate })
            };

            const response = await api.get('/reports/pending-orders-list', { params });
            const orders = response.data.orders || [];
            setOrdersList(orders);
            // Select all orders by default
            setSelectedOrderIds(orders.map(o => o._id));
            setOrdersLoaded(true);
            setOrderSelectionOpen(true);
        } catch (error) {
            console.error('Error fetching orders list:', error);
            setSnackbar({
                open: true,
                message: 'Failed to fetch orders list',
                severity: 'error'
            });
        } finally {
            setLoadingOrders(false);
        }
    };

    // Toggle single order selection
    const handleToggleOrder = (orderId) => {
        setSelectedOrderIds(prev =>
            prev.includes(orderId)
                ? prev.filter(id => id !== orderId)
                : [...prev, orderId]
        );
    };

    // Select all orders
    const handleSelectAll = () => {
        setSelectedOrderIds(ordersList.map(o => o._id));
    };

    // Deselect all orders
    const handleDeselectAll = () => {
        setSelectedOrderIds([]);
    };

    // Confirm order selection and close dialog
    const handleConfirmSelection = () => {
        setOrderSelectionOpen(false);
    };

    const validateFilters = () => {
        const report = reportTypes.find(r => r.id === selectedReport);

        // Check if location is required
        if (report.requiresLocation) {
            // If custom location is allowed and selected, check customLocation field
            if (report.allowCustomLocation && filters.location === 'custom') {
                if (!filters.customLocation || !filters.customLocation.trim()) {
                    setSnackbar({
                        open: true,
                        message: 'Please enter a party location',
                        severity: 'error'
                    });
                    return false;
                }
            } else if (!filters.location) {
                // Regular location validation
                setSnackbar({
                    open: true,
                    message: `Please select a ${report.locationLabel.toLowerCase()}`,
                    severity: 'error'
                });
                return false;
            }
        }

        return true;
    };

    const generateReport = async () => {
        if (!validateFilters()) {
            return;
        }

        setIsGenerating(true);
        setProgress(0);

        // Simulate progress (since we can't track actual PDF generation progress)
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + 10;
            });
        }, 300);

        try {
            const report = reportTypes.find(r => r.id === selectedReport);

            // Build query params
            const params = {
                // Use custom location if selected, otherwise use regular location
                location: (report.allowCustomLocation && filters.location === 'custom')
                    ? filters.customLocation.trim()
                    : filters.location,
                ...(filters.startDate && { startDate: filters.startDate }),
                ...(filters.endDate && { endDate: filters.endDate }),
                ...(report.showProductLocationFilter && filters.productLocation && { productLocation: filters.productLocation })
            };

            // Add selected order IDs for pending orders report (if orders have been loaded and some are selected)
            if (selectedReport === 'pending-orders' && ordersLoaded && selectedOrderIds.length > 0 && selectedOrderIds.length < ordersList.length) {
                params.orderIds = selectedOrderIds.join(',');
            }

            // Make API call to download PDF
            const response = await api.get(`/reports/${selectedReport}`, {
                params,
                responseType: 'blob' // Important for downloading binary data
            });

            clearInterval(progressInterval);
            setProgress(100);

            // Create blob URL and trigger download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename
            const reportName = selectedReport.replace(/-/g, '_');
            const timestamp = new Date().toISOString().split('T')[0];
            const locationForFilename = (report.allowCustomLocation && filters.location === 'custom')
                ? filters.customLocation.trim().replace(/\s+/g, '_')
                : filters.location;
            link.download = `${reportName}_${locationForFilename}_${timestamp}.pdf`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSnackbar({
                open: true,
                message: 'Report generated successfully!',
                severity: 'success'
            });

            // Reset progress after a delay
            setTimeout(() => {
                setProgress(0);
            }, 2000);

        } catch (error) {
            clearInterval(progressInterval);
            console.error('Error generating report:', error);

            let errorMessage = 'Error generating report';
            if (error.response?.data) {
                // Try to parse error message from blob
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    errorMessage = errorData.message || errorMessage;
                } catch (e) {
                    errorMessage = 'Error generating report. Please check your filters and try again.';
                }
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });

            setProgress(0);
        } finally {
            setIsGenerating(false);
        }
    };

    const selectedReportConfig = reportTypes.find(r => r.id === selectedReport);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 0.5
                    }}
                >
                    Reports Center
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Generate detailed PDF reports for your business operations
                </Typography>
            </Box>

            {/* Report Type Selection */}
            {!selectedReport ? (
                <Grid container spacing={3}>
                    {reportTypes.map((report) => (
                        <Grid item xs={12} md={4} key={report.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 6
                                    }
                                }}
                                onClick={() => handleReportSelect(report.id)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                        {report.icon}
                                    </Box>
                                    <Typography variant="h6" align="center" gutterBottom sx={{ fontWeight: 600 }}>
                                        {report.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        {report.description}
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                                    <Button variant="outlined" size="small">
                                        Select Report
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        {selectedReportConfig.icon}
                        <Box sx={{ ml: 2, flexGrow: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                {selectedReportConfig.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedReportConfig.description}
                            </Typography>
                        </Box>
                        <Button
                            variant="text"
                            onClick={() => setSelectedReport('')}
                            sx={{ ml: 2 }}
                        >
                            Change Report
                        </Button>
                    </Box>

                    {/* Filters Section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Report Filters
                        </Typography>
                        <Grid container spacing={2}>
                            {/* Location Filter */}
                            <Grid item xs={12} sm={selectedReportConfig.allowCustomLocation ? 6 : 4}>
                                <FormControl fullWidth required>
                                    <InputLabel>{selectedReportConfig.locationLabel}</InputLabel>
                                    <Select
                                        value={filters.location}
                                        onChange={(e) => handleFilterChange('location', e.target.value)}
                                        label={selectedReportConfig.locationLabel}
                                    >
                                        {selectedReportConfig.locations.map((location) => (
                                            <MenuItem key={location} value={location}>
                                                {location}
                                            </MenuItem>
                                        ))}
                                        {selectedReportConfig.allowCustomLocation && (
                                            <MenuItem value="custom">
                                                <em>Enter Custom Location...</em>
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Custom Location Input - shown when 'custom' is selected */}
                            {selectedReportConfig.allowCustomLocation && filters.location === 'custom' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Enter Party Location"
                                        value={filters.customLocation}
                                        onChange={(e) => handleFilterChange('customLocation', e.target.value)}
                                        required
                                        placeholder="e.g., Jaipur, Delhi, etc."
                                        helperText="Enter the city or location name"
                                    />
                                </Grid>
                            )}

                            {/* Product Location Filter - for filtering orders by product inventory location */}
                            {selectedReportConfig.showProductLocationFilter && (
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Product Location (Optional)</InputLabel>
                                        <Select
                                            value={filters.productLocation}
                                            onChange={(e) => handleFilterChange('productLocation', e.target.value)}
                                            label="Product Location (Optional)"
                                        >
                                            <MenuItem value="">All Locations</MenuItem>
                                            {STORAGE_LOCATIONS.filter(loc => loc !== 'All').map((location) => (
                                                <MenuItem key={location} value={location}>
                                                    {location}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        Filter to show only orders containing products from this inventory location
                                    </Typography>
                                </Grid>
                            )}

                            {/* Start Date Filter */}
                            <Grid item xs={12} sm={selectedReportConfig.allowCustomLocation && filters.location !== 'custom' ? 3 : 4}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Start Date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Optional: Leave empty for all dates"
                                />
                            </Grid>

                            {/* End Date Filter */}
                            <Grid item xs={12} sm={selectedReportConfig.allowCustomLocation && filters.location !== 'custom' ? 3 : 4}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="End Date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Optional: Leave empty for all dates"
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Order Selection Section - only for pending orders report */}
                    {selectedReport === 'pending-orders' && filters.location && (
                        <Box sx={{ mb: 3 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Select Orders (Optional)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Choose which orders to include in the report. By default, all orders are included.
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {ordersLoaded && (
                                        <Chip
                                            label={`${selectedOrderIds.length} of ${ordersList.length} orders selected`}
                                            color={selectedOrderIds.length === ordersList.length ? 'primary' : 'warning'}
                                            variant="outlined"
                                        />
                                    )}
                                    <Button
                                        variant="outlined"
                                        startIcon={loadingOrders ? <CircularProgress size={20} /> : <FilterListIcon />}
                                        onClick={fetchOrdersList}
                                        disabled={loadingOrders || !filters.location}
                                    >
                                        {loadingOrders ? 'Loading...' : ordersLoaded ? 'Change Selection' : 'Select Orders'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Progress Bar */}
                    {isGenerating && (
                        <Box sx={{ mb: 3 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <strong>Generating report...</strong> Please do not leave this page until the download completes.
                            </Alert>
                            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Progress: {progress}%
                            </Typography>
                        </Box>
                    )}

                    {/* Generate Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setSelectedReport('')}
                            disabled={isGenerating}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={generateReport}
                            disabled={
                                isGenerating ||
                                !filters.location ||
                                (selectedReportConfig.allowCustomLocation && filters.location === 'custom' && !filters.customLocation.trim())
                            }
                            sx={{ px: 4 }}
                        >
                            {isGenerating ? 'Generating...' : 'Generate PDF Report'}
                        </Button>
                    </Box>

                    {/* Info Box */}
                    <Box sx={{ mt: 3 }}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Note:</strong> Report generation may take a few moments depending on the amount of data.
                                The PDF will be downloaded automatically when ready.
                            </Typography>
                        </Alert>
                    </Box>
                </Paper>
            )}

            {/* Order Selection Dialog */}
            <Dialog
                open={orderSelectionOpen}
                onClose={() => setOrderSelectionOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { maxHeight: '80vh' }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Select Orders for Report</Typography>
                        <Chip
                            label={`${selectedOrderIds.length} / ${ordersList.length} selected`}
                            sx={{ bgcolor: 'white', color: 'primary.main' }}
                        />
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {/* Select/Deselect All Controls */}
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CheckBoxIcon />}
                                onClick={handleSelectAll}
                            >
                                Select All
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CheckBoxOutlineBlankIcon />}
                                onClick={handleDeselectAll}
                            >
                                Deselect All
                            </Button>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                                Click on an order to toggle selection
                            </Typography>
                        </Box>
                    </Box>

                    {/* Orders List */}
                    {ordersList.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No pending orders found for the selected filters.
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {ordersList.map((order, index) => (
                                <ListItem
                                    key={order._id}
                                    disablePadding
                                    divider={index < ordersList.length - 1}
                                >
                                    <ListItemButton
                                        onClick={() => handleToggleOrder(order._id)}
                                        selected={selectedOrderIds.includes(order._id)}
                                        sx={{
                                            '&.Mui-selected': {
                                                bgcolor: 'primary.light',
                                                '&:hover': {
                                                    bgcolor: 'primary.light'
                                                }
                                            }
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedOrderIds.includes(order._id)}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={order.partyName}
                                            secondary={order.displayDate}
                                            primaryTypographyProps={{ fontWeight: 500 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOrderSelectionOpen(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmSelection}
                        variant="contained"
                        disabled={selectedOrderIds.length === 0}
                    >
                        Confirm Selection ({selectedOrderIds.length} orders)
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ReportsPage;
